import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import StructureChip from './StructureChip';

type BulkDeletionConfirmationDialogProps = {
    open: boolean;
    deletableRois: readonly RoiReadModel[];
    nonDeletableRois: readonly RoiReadModel[];
    onConfirm: () => void;
    onCancel: () => void;
};

/**
 * Irreversible bulk-delete gate. The full deletable name list is the safety
 * surface for members hidden by the list filters, so it always enumerates
 * every structure that will be deleted.
 */
export default function BulkDeletionConfirmationDialog({
    open,
    deletableRois,
    nonDeletableRois,
    onConfirm,
    onCancel,
}: BulkDeletionConfirmationDialogProps) {
    // Block content lives inside ConfirmationDialog's <p>, so everything in
    // the message (chips included) renders as spans (valid nesting).
    const message = (
        <Box component="span" display="block">
            <FormattedMessage
                id="structure.bulk.deletionConfirmation"
                values={{ count: deletableRois.length }}
            />
            <Box
                component="span"
                display="flex"
                flexWrap="wrap"
                gap={0.5}
                sx={{ maxHeight: 160, overflowY: 'auto', mt: 1.5 }}
                data-testid="confirm-chip-list"
            >
                {deletableRois.map((roi) => (
                    <StructureChip
                        key={roi.roiNumber}
                        roi={roi}
                        testId={`confirm-chip-${roi.roiNumber}`}
                    />
                ))}
            </Box>
            {nonDeletableRois.length > 0 && (
                <Box component="span" display="block" mt={1.5}>
                    <Typography
                        component="span"
                        display="block"
                        variant="body2"
                        color="text.secondary"
                    >
                        <FormattedMessage
                            id="structure.bulk.deletionBlocked"
                            values={{ count: nonDeletableRois.length }}
                        />
                    </Typography>
                    <Box
                        component="span"
                        display="flex"
                        flexWrap="wrap"
                        gap={0.5}
                        sx={{ maxHeight: 96, overflowY: 'auto', mt: 1 }}
                        data-testid="confirm-blocked-chip-list"
                    >
                        {nonDeletableRois.map((roi) => (
                            <StructureChip
                                key={roi.roiNumber}
                                roi={roi}
                                testId={`confirm-chip-${roi.roiNumber}`}
                                isLocked
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );

    return (
        <ConfirmationDialog
            open={open}
            onCancel={onCancel}
            onConfirm={onConfirm}
            mode="error"
            title={
                <FormattedMessage
                    id="structure.bulk.deletionTitle"
                    values={{ count: deletableRois.length }}
                />
            }
            message={message}
            cancelText={<FormattedMessage id="cancel" />}
            confirmText={
                <FormattedMessage
                    id="structure.bulk.deleteConfirm"
                    values={{ count: deletableRois.length }}
                />
            }
        />
    );
}
